import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useLanguage } from '@/hooks/use-language';
import { useToast } from '@/hooks/use-toast';
import { User } from '@/types/auth';
import { useAuth } from '@/hooks/use-auth';

interface EditProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User;
  forceCompletion?: boolean;
}

export function EditProfileDialog({ open, onOpenChange, user, forceCompletion = false }: EditProfileDialogProps) {
  const { language } = useLanguage();
  const { toast } = useToast();
  const { updateProfile } = useAuth();
  const [firstName, setFirstName] = useState(user.firstName);
  const [lastName, setLastName] = useState(user.lastName ?? '');
  const [phone, setPhone] = useState(user.phone ?? '');
  const [birthDate, setBirthDate] = useState(user.birthDate ?? '');
  const [streetAddress, setStreetAddress] = useState(user.streetAddress ?? '');
  const [postalCode, setPostalCode] = useState(user.postalCode ?? '');
  const [city, setCity] = useState(user.city ?? '');
  const [isSaving, setIsSaving] = useState(false);

  React.useEffect(() => {
    if (open) {
      setFirstName(user.firstName);
      setLastName(user.lastName ?? '');
      setPhone(user.phone ?? '');
      setBirthDate(user.birthDate ?? '');
      setStreetAddress(user.streetAddress ?? '');
      setPostalCode(user.postalCode ?? '');
      setCity(user.city ?? '');
    }
  }, [open, user]);

  const handleSave = async () => {
    console.log('[EditProfileDialog] Save button clicked. Current values:', {
      firstName,
      lastName,
      phone,
      birthDate,
      streetAddress,
      postalCode,
      city,
    });
    console.log('[EditProfileDialog] Current user state:', {
      userId: user.id,
      currentPhone: user.phone,
    });

    if (!phone.trim()) {
      toast({
        title: language === 'no' ? 'Telefonnummer mangler' : 'Phone number required',
        description: language === 'no'
          ? 'Vennligst legg inn telefonnummeret ditt for å fullføre profilen.'
          : 'Please add your phone number to complete your profile.',
        variant: 'destructive',
      });
      return;
    }

    console.log('[EditProfileDialog] Calling updateProfile...');
    setIsSaving(true);
    const result = await updateProfile({
      firstName,
      lastName,
      phone,
      birthDate: birthDate ? birthDate : null,
      streetAddress: streetAddress ? streetAddress : null,
      postalCode: postalCode ? postalCode : null,
      city: city ? city : null,
    });
    setIsSaving(false);

    console.log('[EditProfileDialog] updateProfile result:', result);

    if (!result.success) {
      console.error('EditProfileDialog: profile update failed', result.error);
      toast({
        title: language === 'no' ? 'Feil' : 'Error',
        description: result.error || (language === 'no' ? 'Kunne ikke oppdatere profilen.' : 'Unable to update profile.'),
        variant: 'destructive',
      });
      return;
    }

    console.log('[EditProfileDialog] Profile updated successfully! Closing dialog.');
    toast({
      title: language === 'no' ? 'Profil oppdatert' : 'Profile updated',
      description: language === 'no' ? 'Dine endringer er lagret.' : 'Your changes have been saved.',
    });
    onOpenChange(false);
  };

  const handleCancel = () => {
    setFirstName(user.firstName);
    setLastName(user.lastName ?? '');
    setPhone(user.phone ?? '');
    setBirthDate(user.birthDate ?? '');
    setStreetAddress(user.streetAddress ?? '');
    setPostalCode(user.postalCode ?? '');
    setCity(user.city ?? '');
    onOpenChange(false);
  };

  const handleDialogChange = (nextOpen: boolean) => {
    if (!nextOpen && forceCompletion && !phone.trim()) {
      toast({
        title: language === 'no' ? 'Telefonnummer mangler' : 'Phone number required',
        description: language === 'no'
          ? 'Du må registrere telefonnummeret ditt før du lukker dette vinduet.'
          : 'Please provide your phone number before closing this dialog.',
        variant: 'destructive',
      });
      return;
    }

    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {language === 'no' ? 'Rediger Profil' : 'Edit Profile'}
          </DialogTitle>
          <DialogDescription>
            {language === 'no' 
              ? 'Oppdater din profilinformasjon. Telefonnummer er påkrevd.' 
              : 'Update your profile information. Phone number is required.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
          <Label htmlFor="firstName">
            {language === 'no' ? 'Fornavn' : 'First Name'}
          </Label>
          <Input
            id="firstName"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder={language === 'no' ? 'Skriv inn fornavn' : 'Enter first name'}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && firstName.trim() && !isSaving) {
                e.preventDefault();
                void handleSave();
              }
            }}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="lastName">
            {language === 'no' ? 'Etternavn' : 'Last Name'}
          </Label>
          <Input
            id="lastName"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder={language === 'no' ? 'Skriv inn etternavn' : 'Enter last name'}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone">
            {language === 'no' ? 'Telefon' : 'Phone'}
          </Label>
          <Input
            id="phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder={language === 'no' ? 'Skriv inn telefonnummer' : 'Enter phone number'}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="birthDate">
            {language === 'no' ? 'Fødselsdato' : 'Birth Date'}
          </Label>
          <Input
            id="birthDate"
            type="date"
            value={birthDate || ''}
            onChange={(e) => setBirthDate(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="streetAddress">
            {language === 'no' ? 'Adresse' : 'Street Address'}
          </Label>
          <Input
            id="streetAddress"
            value={streetAddress}
            onChange={(e) => setStreetAddress(e.target.value)}
            placeholder={language === 'no' ? 'Skriv inn adresse' : 'Enter street address'}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="postalCode">
              {language === 'no' ? 'Postnummer' : 'Postal Code'}
            </Label>
            <Input
              id="postalCode"
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value)}
              placeholder={language === 'no' ? 'Postnummer' : 'Postal code'}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="city">
              {language === 'no' ? 'By' : 'City'}
            </Label>
            <Input
              id="city"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder={language === 'no' ? 'By' : 'City'}
            />
          </div>
        </div>

          <div className="space-y-2">
            <Label htmlFor="userId">
              {language === 'no' ? 'Bruker ID' : 'User ID'}
            </Label>
            <Input
              id="userId"
              value={user.id}
              disabled
            className="bg-muted"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="joinDate">
            {language === 'no' ? 'Medlem siden' : 'Member since'}
            </Label>
            <Input
              id="joinDate"
              value={user.createdAt.toLocaleDateString()}
              disabled
              className="bg-muted"
            />
          </div>
        </div>

        <DialogFooter>
          {!forceCompletion && (
            <Button variant="outline" onClick={handleCancel} disabled={isSaving}>
              {language === 'no' ? 'Avbryt' : 'Cancel'}
            </Button>
          )}
          <Button onClick={handleSave} disabled={!firstName.trim() || !phone.trim() || isSaving}>
            {language === 'no' ? 'Lagre' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
